import { RecurringDonation, Designation, PaymentMethod } from "../types"
import { Badge, Heading } from "@planningcenter/doxy-web"
import { formattedCents } from "source/giving/utils/money"
import { PaymentMethodIcon } from "source/payments/components/icons/PaymentMethodIcon"
import { AlertMessage } from "source/shared/components"
import { date } from "@churchcenter/datetime-fmt"

export default function RecurringDonationDetails({
  recurringDonation,
  paymentMethods,
}: {
  recurringDonation: RecurringDonation
  paymentMethods: PaymentMethod[]
}) {
  const designations: Designation[] = recurringDonation.designations
  const paymentMethod = paymentMethods.find(
    (paymentMethod) => paymentMethod.id === recurringDonation.payment_method_id,
  )
  const donationTotalAmount = designations.reduce(
    (total, designation) => total + designation.amount_cents,
    0,
  )

  const nextOccurenceDate = recurringDonation.next_occurrence
    ? (() => {
        const nextDate = new Date(recurringDonation.next_occurrence)
        const isCurrentYear =
          nextDate.getFullYear() === new Date().getFullYear()
        return date(recurringDonation.next_occurrence, {
          style: "standard",
          year: !isCurrentYear,
        })
      })()
    : null

  const recurringDonationHeadingText = `${formattedCents(donationTotalAmount, {
    noCentsIfWhole: true,
  })}${nextOccurenceDate ? ` on ${nextOccurenceDate}` : ""}`

  const isOnHold =
    recurringDonation.on_hold_indefinitely ||
    (recurringDonation.release_hold_at &&
      new Date(recurringDonation.release_hold_at) > new Date())

  return (
    <div className="d-f fd-c g-2">
      <div className="d-f fd-c g-2 action-drawer">
        <div className="d-f fd-c g-4p">
          <div className="d-f ai-c g-1">
            <Heading
              level={3}
              size={4}
              text={recurringDonationHeadingText}
              color="tint1"
            />
            {isOnHold && <Badge theme="warning" text="On hold" />}
          </div>
          <div className="fs-13 fw-600 c-tint2">
            {recurringDonation.humanized_schedule.toUpperCase()}
          </div>
        </div>
        <div className="d-f fd-c g-1">
          {designations.map((designation) => {
            return (
              <div className="d-f g-1" key={designation.id}>
                <Badge text={designation.fund.name || "Name"} />
                <span>
                  {formattedCents(designation.amount_cents, {
                    noCentsIfWhole: true,
                  })}
                </span>
              </div>
            )
          })}
        </div>
        {!paymentMethod ? (
          <AlertMessage
            type="error"
            icon="general#exclamation-triangle"
            className="mb-0"
          >
            Donation needs payment method
          </AlertMessage>
        ) : (
          <div className="d-f ai-c">
            <PaymentMethodIcon
              brand={paymentMethod.brand}
              isCard={paymentMethod.method_type === "card"}
            />
            <span className="payment-method-badge__meta fs-4">
              {paymentMethod?.brand} ...{paymentMethod?.last4}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
